import React, { useMemo } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { WidgetTypeInfo } from './widget.types.config';
import { DataSourceItem } from './data.sources';

interface WidgetConfirmationProps {
  widgetTypeInfo?: WidgetTypeInfo;
  title: string;
  config?: any; // Конфигурация виджета с источниками данных
}

/**
 * Компонент для подтверждения создания виджета (Шаг 3)
 */
const WidgetConfirmation: React.FC<WidgetConfirmationProps> = ({
  widgetTypeInfo,
  title,
  config,
}) => {
  const translate = useTranslate();

  // Извлекаем источники данных из конфигурации
  const dataSources: DataSourceItem[] = useMemo(() => {
    // Для виджетов с множественными источниками (chart, table, schema)
    if (config?.dataSource?.items) {
      return config.dataSource.items;
    }
    // Для виджетов с одним источником (gauge)
    if (config?.dataSource && typeof config.dataSource === 'object' && 'paramId' in config.dataSource) {
      return [config.dataSource as DataSourceItem];
    }
    return [];
  }, [config?.dataSource]);

  const getObjectDisplay = (item: DataSourceItem): string => {
    if (item.deviceObjectName) {
      return item.deviceObjectName;
    }
    return '—'; // Прочерк если объект не указан
  };

  const getDeviceDisplay = (item: DataSourceItem): string => {
    if (item.deviceName && item.deviceImei) {
      return `${item.deviceName} (${item.deviceImei})`;
    }
    if (item.deviceName) {
      return item.deviceName;
    }
    return `ID: ${item.deviceId.substring(0, 8)}...`;
  };

  const getArchiveTypeDisplay = (item: DataSourceItem): string => {
    if (item.archiveTypeName) {
      return item.archiveTypeName;
    }
    return `ID: ${item.archiveTypeId.substring(0, 8)}...`;
  };

  const getParamDisplay = (item: DataSourceItem): string => {
    const parts: string[] = [];
    
    if (item.paramName) {
      parts.push(item.paramName);
    }
    
    if (item.paramVarname) {
      parts.push(`(${item.paramVarname})`);
    }
    
    if (item.paramUnit) {
      parts.push(`[${item.paramUnit}]`);
    }
    
    if (parts.length > 0) {
      return parts.join(' ');
    }
    
    return `ID: ${item.paramId.substring(0, 8)}...`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {translate('resources.dashboards.widget_wizard.confirmation')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {translate('resources.dashboards.widget_wizard.confirmation_hint')}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Иконка и тип виджета */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box 
                sx={{ 
                  fontSize: '48px', 
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.light',
                  borderRadius: 2,
                  p: 2,
                  opacity: 0.9
                }}
              >
                {widgetTypeInfo?.icon}
              </Box>
              <Box>
                <Typography variant="h6">
                  {widgetTypeInfo?.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {widgetTypeInfo?.description}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          {/* Название виджета */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                borderTop: 1, 
                borderColor: 'divider', 
                pt: 2,
                mt: 1
              }}
            >
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {translate('resources.dashboards.widget_wizard.name_label')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {title}
              </Typography>
            </Box>
          </Grid>
          
          {/* Размер виджета */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                borderTop: 1, 
                borderColor: 'divider', 
                pt: 2
              }}
            >
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {translate('resources.dashboards.widget_wizard.size_label')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {translate('resources.dashboards.widget_wizard.size_value', { 
                  width: widgetTypeInfo?.defaultSize.w, 
                  height: widgetTypeInfo?.defaultSize.h 
                })}
              </Typography>
            </Box>
          </Grid>

          {/* Выбранные источники данных */}
          {dataSources.length > 0 && (
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  borderTop: 1, 
                  borderColor: 'divider', 
                  pt: 2
                }}
              >
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {translate('resources.dashboards.widget_wizard.selected_data_sources')} 
                  ({dataSources.length}):
                </Typography>
                
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {dataSources.map((item, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={`#${index + 1}`} 
                            size="small" 
                            color="primary" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ pl: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>{translate('resources.dashboards.widget_wizard.object')}:</strong>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                            {getObjectDisplay(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>{translate('resources.dashboards.widget_wizard.device')}:</strong>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                            {getDeviceDisplay(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>{translate('resources.dashboards.widget_wizard.archive_type')}:</strong>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                            {getArchiveTypeDisplay(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>{translate('resources.dashboards.widget_wizard.param')}:</strong>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                            {getParamDisplay(item)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default WidgetConfirmation;
